import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  MoreHorizontal,
  Boxes,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ModelModal } from '@/components/trading/model-modal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface TradingModel {
  id: string;
  brandId: string;
  code: string;
  name: string;
  slug: string;
  description?: string | null;
  image?: string | null;
  isActive: boolean;
  sortOrder: number;
  brand: {
    id: string;
    code: string;
    name: string;
  };
  _count: {
    products: number;
  };
}

export function TradingModelsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('MODELS', 'CREATE');
  const canEdit = usePermission('MODELS', 'EDIT');
  const canDelete = usePermission('MODELS', 'DELETE');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<TradingModel | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  // Get brands for filter
  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = brandsData?.brands ?? [];

  const { data, isLoading, error } = trpc.tradingModel.list.useQuery({
    search: debouncedSearch || undefined,
    brandId: brandFilter,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingModel.delete.useMutation({
    onSuccess: () => {
      utils.tradingModel.list.invalidate();
      utils.tradingBrand.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setModelToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleAddModel = () => {
    setEditingModel(undefined);
    setIsModalOpen(true);
  };

  const handleEditModel = (model: TradingModel) => {
    setEditingModel(model);
    setIsModalOpen(true);
  };

  const handleDeleteModel = (id: string) => {
    setModelToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (modelToDelete) {
      deleteMutation.mutate(modelToDelete);
    }
  };

  const models = data?.models ?? [];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('models.title')}</h1>
          <p className="text-muted-foreground">
            {t('models.subtitle')}
            {models.length > 0 && ` (${models.length} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleAddModel}>
            <Plus className="mr-2 h-4 w-4" />
            {t('models.addModel')}
          </Button>
        )}
      </div>

      {/* Model Modal */}
      <ModelModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        model={editingModel}
        defaultBrandId={brandFilter}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('models.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('models.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
        <select
          value={brandFilter ?? ''}
          onChange={(e) => setBrandFilter(e.target.value || undefined)}
          className="h-8 border border-border bg-background px-3 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
        >
          <option value="">{t('models.allBrands')}</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.code} - {brand.name}
            </option>
          ))}
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && models.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Boxes className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('models.noModels')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {debouncedSearch || brandFilter ? t('models.noModels') : t('models.noModelsDesc')}
          </p>
          {!debouncedSearch && !brandFilter && canCreate && (
            <Button className="mt-4" onClick={handleAddModel}>
              <Plus className="mr-2 h-4 w-4" />
              {t('models.addModel')}
            </Button>
          )}
        </div>
      )}

      {/* Models Grid */}
      {!isLoading && !error && models.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {models.map((model) => (
            <div
              key={model.id}
              className="group border border-border bg-card p-5 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {model.image ? (
                    <img
                      src={model.image}
                      alt={model.name}
                      className="h-12 w-12 object-contain"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center bg-blue-100">
                      <Boxes className="h-6 w-6 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{model.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('models.fullCode')}: {model.brand.code}.{model.code}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <DropdownMenuItem onClick={() => handleEditModel(model)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {tCommon('edit')}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteModel(model.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {tCommon('delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  {model.brand.name}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium ${
                    model.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {model.isActive ? t('models.active') : t('models.inactive')}
                </span>
              </div>

              {model.description && (
                <p className="mt-3 text-sm text-muted-foreground line-clamp-2">
                  {model.description}
                </p>
              )}

              <div className="mt-4 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-muted-foreground">
                  {model._count.products} {t('models.products')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
